<template lang="pug">
.rich-text-block
  .rich-text-heading(
    v-if="richTextBlock.heading && richTextBlock.heading.trim()"
    v-html="richTextBlock.heading"
  )
  .rich-text-content(
    v-if="richTextBlock.text && richTextBlock.text.trim()"
    v-html="richTextBlock.text"
  )
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { RichTextBlock as RichTextBlockType } from '../types';

export default defineComponent({
  name: 'RichTextBlock',
  props: {
    block: {
      type: Object as PropType<RichTextBlockType>,
      required: true,
    },
  },
  setup(props) {
    // Computed
    const richTextBlock = computed(() => props.block as RichTextBlockType);

    return {
      richTextBlock,
    };
  },
});
</script>

<style lang="scss" scoped>
.rich-text-block {
  margin-bottom: 48px;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 56px;
  }

  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 64px;
  }
}

.rich-text-heading {
  margin-bottom: 16px;
  color: #1a1a1a;

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-size: 24px;
    font-weight: 800;
    margin: 0 0 8px 0;
    line-height: 1.3;
    color: inherit;

    @media (min-width: 768px) {
      font-size: 28px;
    }

    @media (min-width: 1024px) {
      font-size: 32px;
    }
  }

  :deep(p) {
    font-size: 24px;
    font-weight: 800;
    margin: 0;
    line-height: 1.3;

    @media (min-width: 768px) {
      font-size: 28px;
    }

    @media (min-width: 1024px) {
      font-size: 32px;
    }
  }

  :deep(strong),
  :deep(b) {
    font-weight: 900;
  }

  :deep(em),
  :deep(i) {
    font-style: italic;
  }

  :deep(a) {
    color: #0d9488;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #0f766e;
      text-decoration: underline;
    }
  }
}

.rich-text-content {
  color: #374151;

  :deep(p) {
    font-size: 16px;
    line-height: 1.7;
    margin: 0 0 16px 0;

    &:last-child {
      margin-bottom: 0;
    }

    @media (min-width: 768px) {
      font-size: 17px;
      line-height: 1.8;
    }
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    font-weight: 700;
    margin: 32px 0 16px 0;
    line-height: 1.3;
    color: #1a1a1a;

    &:first-child {
      margin-top: 0;
    }
  }

  :deep(h1) {
    font-size: 32px;
    font-weight: 900;
  }

  :deep(h2) {
    font-size: 28px;
    font-weight: 800;
  }

  :deep(h3) {
    font-size: 24px;
    font-weight: 700;
  }

  :deep(h4) {
    font-size: 20px;
  }

  :deep(h5),
  :deep(h6) {
    font-size: 18px;
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  :deep(li) {
    margin-bottom: 8px;
    line-height: 1.7;
  }

  :deep(a) {
    color: #0d9488;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s;

    &:hover {
      color: #0f766e;
      text-decoration: underline;
    }
  }

  :deep(strong),
  :deep(b) {
    font-weight: 700;
    color: #1a1a1a;
  }

  :deep(em),
  :deep(i) {
    font-style: italic;
  }

  :deep(blockquote) {
    margin: 24px 0;
    padding: 16px 20px;
    border-left: 4px solid #0d9488;
    background: #f0fdfa;
    font-style: italic;
    border-radius: 0 8px 8px 0;
  }

  :deep(code) {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    font-size: 0.9em;
    color: #1a1a1a;
  }

  :deep(pre) {
    background: #f3f4f6;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;

    code {
      background: transparent;
      padding: 0;
    }
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 24px 0;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  :deep(hr) {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 32px 0;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;
    border-radius: 8px;
    overflow: hidden;
  }

  :deep(th),
  :deep(td) {
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }

  :deep(th) {
    font-weight: 700;
    background: #f9fafb;
    color: #1a1a1a;
  }

  :deep(td) {
    color: #374151;
  }
}
</style>
